import { z } from 'zod';
import { dateSchema, integerSchema } from '../validation';

/**
 * Type of payment duration.
 *
 * - `immediate` — due on receipt; `value` is `0`.
 * - `day` / `week` / `month` — `value` is the number of days / weeks /
 *   months (e.g. "net 30 days" → `{ type: 'day', value: 30 }`).
 * - `manual` — there is no fixed cadence; the user issues the
 *   invoice (or releases the payment) manually. Common for invoice
 *   issuance where an Org cannot reliably bill weekly/monthly.
 *   `value` is ignored / kept at `0`.
 */
export const paymentDurationTypeSchema = z.enum(['immediate', 'day', 'week', 'month', 'manual']);
export type PaymentDurationType = z.infer<typeof paymentDurationTypeSchema>;

/**
 * How the buyer settles each invoice.
 *
 * - `bank_transfer` — wire / SADAD / online bank transfer. Default for
 *   B2B since most Hero customers settle via bank.
 * - `cash` — cash-on-delivery (or any out-of-band cash settlement).
 *   Common for individual customers and small fleets.
 *
 * Lives at the contract layer (`orderContractPaymentSchema.method`) so
 * each leg in a brokered chain can carry its own method — e.g. a
 * carrier's leg paid by bank while the freelancer's pickup-leg is
 * settled in cash. Org-level defaults can populate this at order
 * creation; the user can override per-order in the price drawer.
 */
export const paymentMethodSchema = z.enum(['bank_transfer', 'cash']);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const termDurationSchema = z.object({
  /**
   * term duration value is a number such 1,2,3,4,5 etc
   *
   * based on the duration type, we will determine the exact term time such 1 day, 2 weeks, 1 month, etc.
   *
   * If the duration type is `immediate` then this value will be `ZERO`.
   *
   * If this number is negative, then it means that the payment was paid before the due date.
   *
   * Otherwise this number will be `Zero` or `positive` number.
   */
  value: integerSchema,
  /** the type of duration such as immediate, day, week, month, etc */
  type: paymentDurationTypeSchema,
  /** the date in which it will be due. */
  dueDate: dateSchema.optional(),
});
export type TermDuration = z.infer<typeof termDurationSchema>;

//* payment terms
export const paymentTermsSchema = z.object({
  /** display to user the payment terms such as `30 Days` */
  displayTerms: z.coerce.string().optional(),

  /** the payment term for when to issue an invoice for the customer */
  invoiceIssuance: termDurationSchema,

  /** the payment term for when the customer will pay us after issuing the invoice */
  timeToPay: termDurationSchema,
});

export type PaymentTerms = z.infer<typeof paymentTermsSchema>;
